import { cn } from "./_utils";

type PaneMaterial = "staged" | "printed" | "dashed";

type PaneProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * Material = commitment state.
   * - `staged` — glass: translucent, floating, shadowed. Waiting on a human.
   * - `printed` — opaque, flat, on the page. Committed history; never hosts
   *   a consequential control.
   * - `dashed` — outline only. Not yet material / deliberately not done.
   */
  material?: PaneMaterial;
  /** Escalate a staged pane's shadow — the primary gate on a screen. */
  prominent?: boolean;
};

/**
 * The material system as one component. Needs a tinted field behind it for
 * glass to read (the shell canvas is `var(--canvas-field)`).
 *
 * @example
 * <Pane material="staged" prominent className="p-[var(--pad-pane)]">…gate…</Pane>
 * <Pane material="printed">…the record…</Pane>
 * <Pane material="dashed">Update the runbook</Pane>
 */
export function Pane({
  material = "printed",
  prominent,
  className,
  ...props
}: PaneProps) {
  return (
    <div
      data-material={material}
      className={cn(
        material === "staged" && "material-glass",
        material === "staged" && prominent && "material-glass--prominent",
        material === "printed" && "material-print",
        material === "dashed" && "material-dashed",
        className
      )}
      {...props}
    />
  );
}

export type { PaneMaterial, PaneProps };
